import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../../context/CartContext';
import CartItem from './CartItem';

const CartContainer = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 1.5rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`;

const Title = styled.h2`
  color: #333;
  margin: 0;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  
  h3 {
    margin-bottom: 1rem;
    color: #555;
  }
  
  p {
    margin-bottom: 2rem;
    color: #777;
  }
`;

const CartItems = styled.div`
  margin-bottom: 2rem;
`;

const CartSummary = styled.div`
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  &.total {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #eee;
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Button = styled.button`
  background-color: ${props => props.primary ? '#4285f4' : 'white'};
  color: ${props => props.primary ? 'white' : '#4285f4'};
  border: ${props => props.primary ? 'none' : '1px solid #4285f4'};
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.primary ? '#3367d6' : '#f5f5f5'};
  }
  
  &:disabled {
    background-color: #b3b3b3;
    border-color: #b3b3b3;
    color: white;
    cursor: not-allowed;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const Cart = () => {
  const { cart, loading, removeFromCart, updateQuantity, clearCart, getCartTotals } = useCart();
  
  if (loading) {
    return <CartContainer>Loading cart...</CartContainer>;
  }
  
  const { subtotal, itemCount } = getCartTotals();
  const shipping = subtotal > 0 ? 5.99 : 0;
  const tax = subtotal * 0.07; // 7% tax
  const total = subtotal + shipping + tax;
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };
  
  if (cart.length === 0) {
    return (
      <CartContainer>
        <CartHeader>
          <Title>Your Cart</Title>
        </CartHeader>
        <EmptyCart>
          <h3>Your cart is empty</h3>
          <p>Looks like you haven't added any items to your cart yet.</p>
          <StyledLink to="/products">
            <Button primary>Browse Products</Button>
          </StyledLink>
        </EmptyCart>
      </CartContainer>
    );
  }
  
  return (
    <CartContainer>
      <CartHeader>
        <Title>Your Cart ({itemCount} {itemCount === 1 ? 'item' : 'items'})</Title>
        <Button onClick={clearCart}>Clear Cart</Button>
      </CartHeader>
      
      <CartItems>
        {cart.map(item => (
          <CartItem
            key={item.id}
            item={item}
            onRemove={() => removeFromCart(item.id)}
            onUpdateQuantity={(quantity) => updateQuantity(item.id, quantity)}
            formatCurrency={formatCurrency}
          />
        ))}
      </CartItems>
      
      <CartSummary>
        <SummaryRow>
          <span>Subtotal</span>
          <span>{formatCurrency(subtotal)}</span>
        </SummaryRow>
        <SummaryRow>
          <span>Shipping</span>
          <span>{formatCurrency(shipping)}</span>
        </SummaryRow>
        <SummaryRow>
          <span>Tax (7%)</span>
          <span>{formatCurrency(tax)}</span>
        </SummaryRow>
        <SummaryRow className="total">
          <span>Total</span>
          <span>{formatCurrency(total)}</span>
        </SummaryRow>
      </CartSummary>
      
      <ButtonGroup>
        <StyledLink to="/products">
          <Button>Continue Shopping</Button>
        </StyledLink>
        <StyledLink to="/checkout">
          <Button primary>Proceed to Checkout</Button>
        </StyledLink>
      </ButtonGroup>
    </CartContainer>
  );
};

export default Cart;
